import { useState, useEffect } from "react";
import {
  Wallet,
  Send,
  Plus,
  Clock,
  Shield,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  Copy,
  ExternalLink,
  X,
  TimerIcon,
  ShieldCheck,
  Nfc,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { GridLoader } from "react-spinners";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { getBalance, getRecentTransactions } from "../services/authapi";
import toast, { Toaster } from "react-hot-toast";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { usePrivy, useMfaEnrollment } from "@privy-io/react-auth";

interface Transaction {
  id: string;
  type: "sent" | "received";
  amount: number;
  from: string;
  to: string;
  date: string;
  hash: string;
}

interface OfframpTransaction {
  id: string;
  status:
    | "TRANSACTION_STATUS_STARTED"
    | "TRANSACTION_STATUS_SUCCESS"
    | "TRANSACTION_STATUS_FAILED";
  asset: string;
  network: string;
  sell_amount: { value: string; currency: string };
  total?: { value: string; currency: string };
  toAddress: string;
  fromAddress?: string;
  tx_hash?: string;
  created_at: string;
}

const paymentLogos = [
  { src: "/applepay-logo.png", alt: "Apple Pay", scale: "scale-150 invert" },
  { src: "/googlepay-logo.png", alt: "Google Pay", scale: "scale-100" },
  { src: "/cashapp-logo.png", alt: "Cash App", scale: "scale-100" },
  { src: "/Stripe_Logo.png", alt: "Stripe", scale: "scale-100" },
  { src: "/mastercard-logo.png", alt: "Mastercard", scale: "scale-120" },
  { src: "/coinbase.svg", alt: "Coinbase", scale: "scale-120" },
  { src: "/crossmint-light.png", alt: "Crossmint", scale: "scale-120" },
];

export function Dashboard() {
  const { user: privyUser } = usePrivy();
  const { showMfaEnrollmentModal } = useMfaEnrollment();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state: RootState) => state.user) as any;
  const username = user?.username || "User";
  const walletAddress = user?.smartWallet || "";
  const adminWallet = import.meta.env.VITE_Admin_Wallet?.toLowerCase() || "";
  const serverUrl = import.meta.env.VITE_SERVERURL;

  const [walletBalance, setWalletBalance] = useState<string>("0");
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingBalance, setLoadingBalance] = useState<boolean>(true);
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(true);
  const [loadingProfiles, setLoadingProfiles] = useState<boolean>(true);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState<string>("");
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  // New state for Offramp status modal
  const [showOfframpStatus, setShowOfframpStatus] = useState(false);
  const [offrampTx, setOfframpTx] = useState<OfframpTransaction | null>(null);
  const [offrampLoading, setOfframpLoading] = useState(false);
  const [offrampError, setOfframpError] = useState<string | null>(null);

  const [addressToUsername, setAddressToUsername] = useState<{
    [address: string]: string | undefined;
  }>({});

  const isLoading =
    loadingBalance || loadingTransactions || loadingProfiles || !user;

  // Check for success redirect from Coinbase Offramp
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("success") === "offramp-redirect") {
      setShowOfframpStatus(true);
      // Clean the URL
      window.history.replaceState({}, "", "/dashboard");
    }
  }, [location]);

  // Fetch Offramp transaction status
  const fetchOfframpStatus = async () => {
    if (!user?.uid) return;

    setOfframpLoading(true);
    setOfframpError(null);

    try {
      const response = await fetch(
        `${serverUrl}/offramp-status?partnerUserRef=${user.uid}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch offramp status");
      }

      const data = await response.json();
      const txList = data.transactions || [];

      if (txList.length > 0) {
        const latestTx = txList[0]; // newest first
        setOfframpTx(latestTx);

        // Stop polling if completed
        if (
          latestTx.status === "TRANSACTION_STATUS_SUCCESS" ||
          latestTx.status === "TRANSACTION_STATUS_FAILED"
        ) {
          clearInterval(pollInterval);
        }
      } else {
        setOfframpTx(null);
      }
    } catch (err: any) {
      console.error("Error fetching offramp status:", err);
      setOfframpError(err.message || "Failed to load status");
    } finally {
      setOfframpLoading(false);
    }
  };

  let pollInterval: NodeJS.Timeout;

  useEffect(() => {
    if (showOfframpStatus && user?.uid) {
      fetchOfframpStatus();
      pollInterval = setInterval(fetchOfframpStatus, 10000); // poll every 10s

      return () => clearInterval(pollInterval);
    }
  }, [showOfframpStatus, user?.uid]);

  useEffect(() => {
    const fetchBalance = async () => {
      if (!walletAddress) {
        toast.error("No wallet address found for the user.");
        setLoadingBalance(false);
        return;
      }

      try {
        const usdcBalance = await getBalance(walletAddress);
        setWalletBalance(usdcBalance);
      } catch (err: any) {
        console.error("Error fetching USDC balance:", err.message);
        toast.error("Failed to fetch wallet balance. Please try again.");
      } finally {
        setLoadingBalance(false);
      }
    };

    if (user) {
      fetchBalance();
    } else {
      setLoadingBalance(false);
    }
  }, [walletAddress, user]);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!walletAddress) {
        toast.error("No wallet address found for the user.");
        setLoadingTransactions(false);
        return;
      }

      try {
        const txs = await getRecentTransactions(walletAddress);

        const filteredTxs = txs.filter((tx: Transaction) => {
          if (tx.type === "sent") {
            return tx.to.toLowerCase() !== adminWallet;
          }
          return true;
        });

        setTransactions(filteredTxs);
      } catch (err: any) {
        console.error("Error fetching transactions:", err.message);
        toast.error("Failed to fetch transactions. Please try again.");
      } finally {
        setLoadingTransactions(false);
      }
    };

    if (user) {
      fetchTransactions();
    } else {
      setLoadingTransactions(false);
    }
  }, [walletAddress, user, adminWallet]);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        const vendorsSnap = await getDocs(collection(db, "vendors"));
        const map: { [address: string]: string | undefined } = {};

        usersSnap.forEach((doc) => {
          const data = doc.data();
          if (data.wallet) {
            map[data.wallet.toLowerCase()] = data.username;
          }
        });

        vendorsSnap.forEach((doc) => {
          const data = doc.data();
          if (data.wallet) {
            map[data.wallet.toLowerCase()] = data.name;
          }
        });

        setAddressToUsername(map);
      } catch (err: any) {
        console.error("Error fetching profiles:", err.message);
        toast.error("Failed to fetch profiles. Please try again.");
      } finally {
        setLoadingProfiles(false);
      }
    };

    fetchProfiles();
  }, []);

  const truncateAddress = (address: string): string => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getCounterpartyDisplay = (tx: Transaction): string => {
    const counterparty =
      tx.type === "received" ? tx.from.toLowerCase() : tx.to.toLowerCase();
    return addressToUsername[counterparty] || "External";
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const viewOnBlockExplorer = (hash: string) => {
    window.open(`https://basescan.org/tx/${hash}`, "_blank");
  };

  const handleEnrollMfa = () => {
    if (!privyUser?.wallet) {
      toast.error("No embedded wallet found. Create one first.");
      return;
    }
    showMfaEnrollmentModal();
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error("Please enter a valid amount.");
      return;
    }

    if (parseFloat(withdrawAmount) > parseFloat(walletBalance)) {
      toast.error("Amount exceeds available balance.");
      return;
    }

    setIsWithdrawing(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SERVERURL}/create-offramp-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            walletAddress: walletAddress.toLowerCase(),
            clientIp: "0.0.0.0",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const { sessionToken } = await response.json();
      if (!sessionToken) {
        throw new Error("No session token returned");
      }
      console.log(user)

   const offrampUrl =
     "https://pay.coinbase.com/v3/sell/input" +
     `?sessionToken=${sessionToken}` +
     `&partnerUserId=${user?.uid}` + // ← Change from partnerUserRef to partnerUserId
     `&redirectUrl=${encodeURIComponent(
       "https://cashtap.cash/dashboard?success=offramp-redirect"
     )}`;
      const width = 480;
      const height = 720;
      const left = window.screenX + (window.innerWidth - width) / 2;
      const top = window.screenY + (window.innerHeight - height) / 2;

      window.open(
        offrampUrl,
        "coinbase-offramp",
        `width=${width},height=${height},left=${left},top=${top},resizable=yes`
      );

      toast.success("Opening Coinbase to complete withdrawal...");
      setShowWithdraw(false);
      setWithdrawAmount("");
    } catch (err: any) {
      toast.error(err.message || "Failed to initiate withdraw");
    } finally {
      setIsWithdrawing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <GridLoader color="#61CE70" size={20} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 min-h-screen">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#0F1419",
            color: "#fff",
            border: "1px solid #374151",
            borderRadius: "12px",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "#61CE70",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />

      {/* Enhanced Offramp Status Modal */}
      {showOfframpStatus && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[1000] p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-amber-600/40 rounded-3xl p-8 sm:p-10 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl sm:text-3xl font-black text-white">
                Offramp Request Status
              </h2>
              <button
                onClick={() => setShowOfframpStatus(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            {offrampLoading && !offrampTx && (
              <div className="text-center py-12">
                <GridLoader color="#f59e0b" />
                <p className="text-amber-300 mt-4">
                  Loading transaction status...
                </p>
              </div>
            )}

            {offrampError && (
              <div className="text-center py-8 text-red-400">
                <AlertCircle className="w-16 h-16 mx-auto mb-4" />
                <p>{offrampError}</p>
              </div>
            )}

            {offrampTx && (
              <div className="space-y-6">
                <div className="text-center">
                  {offrampTx.status === "TRANSACTION_STATUS_STARTED" && (
                    <>
                      <Clock className="w-16 h-16 text-amber-400 mx-auto animate-spin-slow" />
                      <p className="text-amber-300 text-xl font-bold mt-4">
                        Pending
                      </p>
                      <p className="text-amber-200/80 mt-2">
                        Send the crypto to the address below to complete the
                        offramp.
                      </p>
                    </>
                  )}
                  {offrampTx.status === "TRANSACTION_STATUS_SUCCESS" && (
                    <>
                      <CheckCircle className="w-16 h-16 text-green-400 mx-auto" />
                      <p className="text-green-400 text-xl font-bold mt-4">
                        Success!
                      </p>
                      <p className="text-green-300 mt-2">
                        Your funds are on their way to your bank account.
                      </p>
                    </>
                  )}
                  {offrampTx.status === "TRANSACTION_STATUS_FAILED" && (
                    <>
                      <AlertCircle className="w-16 h-16 text-red-400 mx-auto" />
                      <p className="text-red-400 text-xl font-bold mt-4">
                        Failed
                      </p>
                      <p className="text-red-300 mt-2">
                        The transaction failed. Please try again.
                      </p>
                    </>
                  )}
                </div>

                <div className="bg-gray-800/50 rounded-2xl p-6 space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Amount to Sell</span>
                    <span className="text-white font-bold">
                      {offrampTx.sell_amount.value}{" "}
                      {offrampTx.sell_amount.currency}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Network</span>
                    <span className="text-white font-bold uppercase">
                      {offrampTx.network}
                    </span>
                  </div>

                  {offrampTx.status === "TRANSACTION_STATUS_STARTED" && (
                    <>
                      <div className="border-t border-gray-700 pt-4">
                        <p className="text-amber-300 font-bold text-lg mb-3">
                          Send to this Coinbase Address:
                        </p>
                        <div className="bg-black/40 rounded-xl p-4 break-all text-white font-mono text-sm">
                          {offrampTx.toAddress}
                        </div>

                        <button
                          onClick={() => copyToClipboard(offrampTx.toAddress)}
                          className="mt-4 w-full py-3 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition"
                        >
                          <Copy className="w-5 h-5" />
                          Copy Address
                        </button>

                        {/* New: Go to Transfer Page Button */}
                        <a
                          href={`/transfer?to=${offrampTx.toAddress}&amount=${offrampTx.sell_amount.value}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 w-full block"
                        >
                          <button className="w-full py-4 bg-[#61CE70] hover:bg-[#50B060] text-white font-bold rounded-xl flex items-center justify-center gap-3 transition shadow-lg">
                            <Send className="w-5 h-5" />
                            Go to Transfer Page (New Tab)
                          </button>
                        </a>

                        {/* Important Note */}
                        <div className="mt-4 p-4 bg-amber-900/30 border border-amber-600/50 rounded-xl">
                          <p className="text-amber-200 text-sm text-center font-medium">
                            <strong>Important:</strong> On the Transfer page,
                            select{" "}
                            <span className="text-amber-300 font-bold">
                              "External"
                            </span>{" "}
                            as the recipient, paste the Coinbase address above,
                            and send the exact amount.
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  {offrampTx.tx_hash && (
                    <div className="border-t border-gray-700 pt-4">
                      <p className="text-gray-400 mb-2">Transaction Hash</p>
                      <div className="flex items-center justify-between">
                        <span className="text-white font-mono text-sm break-all">
                          {truncateAddress(offrampTx.tx_hash)}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => copyToClipboard(offrampTx.tx_hash!)}
                            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              viewOnBlockExplorer(offrampTx.tx_hash!)
                            }
                            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setShowOfframpStatus(false)}
                  className="w-full py-4 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-2xl transition"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Wallet Card */}
      <div className="w-full mb-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-950 via-emerald-900 to-teal-950 p-3 sm:p-8 lg:p-10 text-white shadow-2xl border border-emerald-800/50">
          <div className="absolute inset-0 bg-gradient-to-tr from-emerald-600/10 via-transparent to-transparent" />
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-teal-600/10 rounded-full blur-3xl" />

          <div className="absolute top-6 right-6 sm:top-8 sm:right-8">
            <div className="p-2.5 sm:p-3 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 shadow-sm">
              <Nfc className="w-5 h-5 sm:w-6 sm:h-6 text-white/90" />
            </div>
          </div>

          <div className="absolute top-[6rem] left-6 sm:top-[8rem] sm:left-8 lg:top-[7.4rem] lg:left-10">
            <div className="w-12 h-8 sm:w-14 sm:h-9 lg:w-16 lg:h-10 bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 rounded-lg shadow-lg border border-amber-200/50 flex items-center justify-center">
              <div className="grid grid-cols-4 gap-0.5">
                {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <div
                    key={i}
                    className="w-1.5 h-2.5 sm:w-2 sm:h-3 bg-amber-700/50 rounded-full"
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="relative z-10 flex items-center gap-4 sm:gap-5">
            <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-gradient-to-br from-emerald-400/40 to-teal-600/30 backdrop-blur-xl border-2 border-emerald-300/40 flex items-center justify-center shadow-lg">
              <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-emerald-400 flex items-center justify-center">
                <span className="text-emerald-950 text-xl sm:text-2xl lg:text-3xl font-black">
                  $
                </span>
              </div>
            </div>

            <div>
              <h1 className="text-xl sm:text-2xl lg:text-4xl font-black tracking-tight">
                Cash<span className="text-emerald-400">Tap</span>
              </h1>
              <p className="text-emerald-300/70 text-[0.65rem] sm:text-xs lg:text-sm font-medium tracking-wider mt-0.5">
                BASE CHAIN • USDC WALLET
              </p>
            </div>
          </div>

          <div className="mt-7 md:ml-0 ml-20 sm:mt-12 lg:mt-14">
            <div className="flex items-end gap-2 sm:gap-3">
              <p className="text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter leading-none">
                {loadingBalance ? (
                  <span className="text-white/40">••••••</span>
                ) : (
                  parseFloat(walletBalance).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                )}
              </p>
              <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-emerald-300 pb-0.5">
                USDC
              </span>
            </div>
            <p className="text-emerald-200/80 text-xs sm:text-sm font-medium mt-1.5">
              Available Balance
            </p>
          </div>

          <div className="mt-2 sm:mt-10 lg:mt-5 flex flex-col sm:flex-row sm:items-end justify-between gap-4 sm:gap-6">
            <div className="space-y-1">
              <p className="text-base sm:text-lg lg:text-xl font-bold tracking-wide">
                {username
                  ? username.charAt(0).toUpperCase() + username.slice(1)
                  : "User"}
              </p>
              <p className="text-[0.65rem] sm:text-xs lg:text-sm text-emerald-300/70 font-medium">
                Member since{" "}
                {user?.joinedAt
                  ? new Date(user.joinedAt).toLocaleDateString("en-US", {
                      month: "short",
                      year: "2-digit",
                    })
                  : "Oct '25"}
              </p>
            </div>

            <div className="bg-gradient-to-r from-emerald-500/20 to-teal-600/20 backdrop-blur-xl px-4 py-2.5 sm:px-5 sm:py-3 rounded-full border border-emerald-400/30 shadow-md flex items-center gap-2 sm:gap-3">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-sm">
                <span className="text-emerald-950 text-base sm:text-lg font-black">
                  $
                </span>
              </div>
              <div className="text-right flex justify-start items-start flex-col">
                <p className="text-emerald-300 font-bold text-[0.65rem] sm:text-xs tracking-widest leading-tight">
                  TAPTAPUP
                </p>
                <p className="text-emerald-400/80 text-[0.55rem] sm:text-[0.65rem] font-semibold leading-tight">
                  DEBIT CARD
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-8 sm:mb-10">
        <button
          onClick={() => navigate("/add-funds")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-[#61CE70]/50 hover:shadow-lg hover:shadow-[#61CE70]/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-[#61CE70]/20 rounded-xl group-hover:bg-[#61CE70]/30 transition-colors">
              <Plus className="w-5 h-5 text-[#61CE70]" />
            </div>
            <span className="font-medium text-xs sm:text-sm">Add Money</span>
          </div>
        </button>

        <button
          onClick={() => navigate("/transfer")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-blue-500/20 rounded-xl group-hover:bg-blue-500/30 transition-colors">
              <Send className="w-5 h-5 text-blue-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">Send Money</span>
          </div>
        </button>

        <button
          onClick={() => setShowWithdraw(true)}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-red-500/50 hover:shadow-lg hover:shadow-red-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-red-500/20 rounded-xl group-hover:bg-red-500/30 transition-colors">
              <ArrowUpRight className="w-5 h-5 text-red-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">Withdraw</span>
          </div>
        </button>

        <button
          onClick={() => navigate("/request")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-purple-500/20 rounded-xl group-hover:bg-purple-500/30 transition-colors">
              <Clock className="w-5 h-5 text-purple-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">
              Payment Request
            </span>
          </div>
        </button>

        <button
          onClick={() => navigate("/wallet")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-blue-500/20 rounded-xl group-hover:bg-blue-500/30 transition-colors">
              <Wallet className="w-5 h-5 text-blue-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">View Wallet</span>
          </div>
        </button>

        <button
          onClick={() => navigate("/analytics")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-orange-500/50 hover:shadow-lg hover:shadow-orange-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-orange-500/20 rounded-xl group-hover:bg-orange-500/30 transition-colors">
              <TimerIcon className="w-5 h-5 text-orange-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">Analytics</span>
          </div>
        </button>

        <button
          onClick={() => navigate("/earn")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-yellow-500/50 hover:shadow-lg hover:shadow-yellow-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-yellow-500/20 rounded-xl group-hover:bg-yellow-500/30 transition-colors">
              <TrendingUp className="w-5 h-5 text-yellow-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">Earn</span>
          </div>
        </button>

        <button
          onClick={() => navigate("/shopping")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-pink-500/50 hover:shadow-lg hover:shadow-pink-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-pink-500/20 rounded-xl group-hover:bg-pink-500/30 transition-colors">
              <Users className="w-5 h-5 text-pink-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">Shopping</span>
          </div>
        </button>

        <button
          onClick={() => setShowAllTransactions(true)}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-purple-500/20 rounded-xl group-hover:bg-purple-500/30 transition-colors">
              <Clock className="w-5 h-5 text-purple-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">History</span>
          </div>
        </button>
      </div>

      {/* Powered By Section */}
      <section
        className="py-0 md:py-16 overflow-hidden"
        aria-label="Trusted payment partners"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-5 md:mb-12">
            <h3 className="inline-block text-4xl sm:text-5xl font-black tracking-tight bg-gradient-to-r from-blue-500 via-cyan-400 to-purple-600 bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient-shift">
              Powered By
            </h3>
          </div>

          <div className="relative rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden">
            <div className="py-10">
              <div className="flex animate-marquee-infinite gap-2 md:gap-20">
                {[...paymentLogos, ...paymentLogos].map((logo, i) => (
                  <div key={i} className="flex-shrink-0 px-1 md:px-4">
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      className={`h-3 md:h-12 ${logo.scale} object-contain transition-all duration-300 hover:scale-125 hover:drop-shadow-2xl cursor-pointer`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#0F1419] to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#0F1419] to-transparent" />
          </div>
        </div>

        <style>{`
          @keyframes marquee-infinite {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-50%); }
          }
          @keyframes gradient-shift {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-marquee-infinite {
            animation: marquee-infinite 22s linear infinite;
          }
          .animate-marquee-infinite:hover {
            animation-play-state: paused;
          }
          .animate-gradient-shift {
            background-size: 200% 200%;
            animation: gradient-shift 6s ease infinite;
          }
          .animate-spin-slow {
            animation: spin-slow 4s linear infinite;
          }
        `}</style>
      </section>

      {/* Recent Activity + Account Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">
              Recent Transactions
            </h3>
            <button
              onClick={() => setShowAllTransactions(true)}
              className="text-[#61CE70] hover:text-[#50B060] text-sm font-medium transition-colors"
            >
              View All
            </button>
          </div>
          {loadingTransactions ? (
            <div className="text-center text-gray-400">
              Loading transactions...
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center text-gray-400">
              No transactions found.
            </div>
          ) : (
            <div className="space-y-4">
              {transactions.slice(0, 2).map((tx) => (
                <div
                  key={tx.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 bg-gray-800/30 rounded-2xl hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex items-start sm:items-center space-x-4 w-full sm:w-auto">
                    <div
                      className={`p-3 rounded-xl shrink-0 ${
                        tx.type === "received"
                          ? "bg-[#61CE70]/20"
                          : "bg-red-500/20"
                      }`}
                    >
                      {tx.type === "received" ? (
                        <ArrowDownRight className="w-5 h-5 text-[#61CE70]" />
                      ) : (
                        <ArrowUpRight className="w-5 h-5 text-red-400" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium capitalize truncate">
                        {tx.type === "received" ? "Deposit" : "Transfer"}
                      </p>
                      <p className="text-gray-400 text-sm break-words">
                        {tx.type === "received" ? (
                          <span>
                            From:{" "}
                            <span className="font-bold text-white uppercase">
                              {getCounterpartyDisplay(tx)}
                            </span>
                          </span>
                        ) : (
                          <span>
                            To:{" "}
                            <span className="font-bold text-white uppercase">
                              {getCounterpartyDisplay(tx)}
                            </span>
                          </span>
                        )}
                      </p>
                      <p className="text-gray-500 text-xs">{tx.date}</p>
                    </div>
                  </div>

                  <div className="mt-4 sm:mt-0 text-left sm:text-right space-y-2 w-full sm:w-auto">
                    <p
                      className={`text-lg font-bold ${
                        tx.type === "received"
                          ? "text-[#61CE70]"
                          : "text-red-400"
                      }`}
                    >
                      {tx.type === "received" ? "+" : "-"}
                      {tx.amount} USDC
                    </p>
                    <div className="flex flex-wrap sm:justify-end gap-2">
                      <button
                        onClick={() => copyToClipboard(tx.hash)}
                        className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors"
                      >
                        <span className="hidden sm:inline">
                          {truncateAddress(tx.hash)}
                        </span>
                        <span className="sm:hidden">Hash</span>
                        <Copy className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => viewOnBlockExplorer(tx.hash)}
                        className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors"
                      >
                        <span>Explorer</span>
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Withdraw Modal */}
        {showWithdraw && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
            <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-6 sm:p-8 w-full sm:max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  Withdraw Amount
                </h3>
                <button
                  onClick={() => {
                    setShowWithdraw(false);
                    setWithdrawAmount("");
                  }}
                  className="p-2 hover:bg-gray-700/50 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    placeholder="Enter amount in USD"
                    className="w-full p-4 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white placeholder-gray-400 text-2xl font-bold text-center focus:outline-none focus:border-[#61CE70]/50 transition"
                  />
                  <p className="text-center text-gray-400 text-sm mt-3">
                    Available: {parseFloat(walletBalance).toFixed(2)} USDC
                  </p>
                </div>

                <button
                  onClick={handleWithdraw}
                  disabled={isWithdrawing}
                  className="w-full bg-[#61CE70] hover:bg-[#50B060] disabled:opacity-70 text-white font-bold py-5 rounded-2xl text-lg transition duration-300 shadow-lg"
                >
                  {isWithdrawing
                    ? "Opening Coinbase..."
                    : "Continue in Coinbase"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* All Transactions Modal */}
        {showAllTransactions && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
            <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-6 sm:p-8 w-full sm:max-w-3xl max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  All Transactions
                </h3>
                <button
                  onClick={() => setShowAllTransactions(false)}
                  className="p-2 hover:bg-gray-700/50 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {transactions.length === 0 ? (
                <div className="text-center text-gray-400 py-6">
                  No transactions found.
                </div>
              ) : (
                <div className="space-y-4">
                  {transactions.map((tx) => (
                    <div
                      key={tx.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 bg-gray-800/30 rounded-2xl hover:bg-gray-800/50 transition-colors"
                    >
                      <div className="flex items-start sm:items-center space-x-4 w-full sm:w-auto">
                        <div
                          className={`p-3 rounded-xl shrink-0 ${
                            tx.type === "received"
                              ? "bg-[#61CE70]/20"
                              : "bg-red-500/20"
                          }`}
                        >
                          {tx.type === "received" ? (
                            <ArrowDownRight className="w-5 h-5 text-[#61CE70]" />
                          ) : (
                            <ArrowUpRight className="w-5 h-5 text-red-400" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium capitalize truncate">
                            {tx.type === "received" ? "Deposit" : "Transfer"}
                          </p>
                          <p className="text-gray-400 text-sm break-words">
                            {tx.type === "received"
                              ? `From: ${getCounterpartyDisplay(tx)}`
                              : `To: ${getCounterpartyDisplay(tx)}`}
                          </p>
                          <p className="text-gray-500 text-xs">{tx.date}</p>
                        </div>
                      </div>

                      <div className="mt-4 sm:mt-0 text-left sm:text-right space-y-2 w-full sm:w-auto">
                        <p
                          className={`text-base sm:text-lg font-bold ${
                            tx.type === "received"
                              ? "text-[#61CE70]"
                              : "text-red-400"
                          }`}
                        >
                          {tx.type === "received" ? "+" : "-"}
                          {tx.amount} USDC
                        </p>
                        <div className="flex flex-wrap sm:justify-end gap-2">
                          <button
                            onClick={() => copyToClipboard(tx.hash)}
                            className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors"
                          >
                            <span className="hidden sm:inline">
                              {truncateAddress(tx.hash)}
                            </span>
                            <span className="sm:hidden">Hash</span>
                            <Copy className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => viewOnBlockExplorer(tx.hash)}
                            className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors"
                          >
                            <span>Explorer</span>
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
          <h3 className="text-xl font-bold text-white mb-6">
            Account Overview
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-2xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-[#61CE70]/20 rounded-lg">
                  <ShieldCheck className="w-5 h-5 text-[#61CE70]" />
                </div>
                <span className="text-white">Wallet MFA (Passkey)</span>
              </div>
              <button
                onClick={handleEnrollMfa}
                className="text-[#61CE70] font-medium hover:text-[#50B060] transition-colors"
              >
                {privyUser?.mfaMethods?.includes("passkey")
                  ? "Manage MFA"
                  : "Enable MFA"}
              </button>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-2xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-[#61CE70]/20 rounded-lg">
                  <Shield className="w-5 h-5 text-[#61CE70]" />
                </div>
                <span className="text-white">KYC Verification</span>
              </div>
              <span className="text-[#61CE70] font-medium">Verified</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-2xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-white">Account Tier</span>
              </div>
              <span className="text-blue-400 font-medium">Premium</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-2xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <span className="text-white">Daily Limit</span>
              </div>
              <span className="text-purple-400 font-medium">$10,000</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
